import { BLOCK_HINT, BLOCK_QUOTE, list, moveToNextLeafBlock } from '@gitbook/doc-core';
import {
    type Block,
    type Change,
    type Document,
    type Fragment,
    type Inline,
    isBlockRangeSelection,
    TextRange,
    type Value,
} from '@gitbook/slate';
import type { Plugin } from '@gitbook/slate-react';

import { isBackspace, isEnter, isModA, isModEnter, selectAllInContainerFirst } from '../common';

export const plugin: Plugin = {
    onKeyDown,
};

/**
 * Handle key down events for tabs.
 */
function onKeyDown(event: KeyboardEvent, change: Change): Change | void {
    const { value } = change;
    if (isBlockRangeSelection(value.selection)) {
        return;
    }

    // Pressing backspace in an empty quote/hint block should remove it.
    if (isBackspace(event) && value.isCollapsed) {
        return onBackspace(event, change);
    }

    // Exit and split the block with "double" enter.
    if (isEnter(event)) {
        return onEnter(event, change);
    }

    // Exit the block with alternate enter.
    if (isModEnter(event)) {
        return onModEnter(event, change);
    }

    if (isModA(event)) {
        event.preventDefault();
        return selectAllInContainerFirst(change, getQuoteOrHintRange);
    }

    // Wrap in a quote when pressing `>'
    if (event.key === '>') {
        return onGreaterThan(event, change);
    }

    return;
}

/**
 * Delete the block if it's empty.
 */
function onBackspace(event: Event, change: Change): Change | void {
    const { value } = change;
    const { startOffset, selection } = value;

    if (!TextRange.isTextRange(selection)) {
        return;
    }

    // ... when not at the beginning of nodes
    if (startOffset > 0) {
        return;
    }

    // ... in a list
    if (list.isSelectionInList(value)) {
        return;
    }

    const currentHintOrQuoteBlock = getCurrentQuoteOrHintBlock(value);
    if (!currentHintOrQuoteBlock) {
        return;
    }

    // Blockquote contains block
    const ancestorBlock = selection.anchorKey
        ? currentHintOrQuoteBlock.getClosestBlock(selection.anchorKey)
        : null;

    // ... more precisely at the beginning of the current item
    if (!ancestorBlock || !selection.isAtStartOf(ancestorBlock)) {
        return;
    }
    // Check that the block only contains 1 element
    if (currentHintOrQuoteBlock.nodes.size !== 1) {
        return;
    }

    // Block is empty, we exit the blockquote
    event.preventDefault();
    return change.unwrapBlock(currentHintOrQuoteBlock.type);
}

/**
 * Enter on an empty block inside a blockquote exit the blockquote.
 */
function onEnter(event: Event, change: Change): Change | void {
    const { value } = change;
    const { startBlock, selection } = value;

    if (!TextRange.isTextRange(selection)) {
        return;
    }

    // ... in a list
    if (list.isSelectionInList(value)) {
        return;
    }

    const currentBlockQuote = getCurrentQuoteOrHintBlock(value);
    if (!currentBlockQuote) {
        return;
    }

    if (startBlock && startBlock.text.length !== 0) {
        return;
    }

    // Block is empty, we exit the blockquote
    event.preventDefault();
    return change.unwrapBlock(currentBlockQuote.type);
}

/**
 * Exit the current block and inserts a paragraph after it.
 */
function onModEnter(event: Event, change: Change): Change | void {
    const { value } = change;

    const blockquote = getCurrentQuoteOrHintBlock(value);
    if (!blockquote) {
        return;
    }

    event.preventDefault();

    moveToNextLeafBlock(change, blockquote);
    return change;
}

/**
 * Wrap current content in a quote when pressing '>'
 */
function onGreaterThan(event: Event, change: Change): Change | void {
    const { value } = change;
    const { selection } = value;

    if (!TextRange.isTextRange(selection)) {
        return;
    }

    if (selection.startOffset !== 0 || selection.isCollapsed) {
        // We only wrap in a quote if you are selecting from the start of a block
        return;
    }

    event.preventDefault();

    change.wrapBlock(BLOCK_QUOTE);

    return change;
}

/**
 * Get the currently selected quote or hint block.
 */
function getCurrentQuoteOrHintBlock(value: Value, block: Block | null = null): Block | null {
    const { document, selection } = value;

    if (!TextRange.isTextRange(selection)) {
        return null;
    }

    block ??= value.startBlock;
    if (!block) {
        return null;
    }

    const ancestors = document.getAncestors(block.key);
    if (!ancestors) {
        return null;
    }
    const closestQuoteOrHintBlock = ancestors.reverse().find(isQuoteOrHintBlock);
    return closestQuoteOrHintBlock && isQuoteOrHintBlock(closestQuoteOrHintBlock)
        ? closestQuoteOrHintBlock
        : null;
}

/**
 * Test if a block is a hint or quote.
 */
function isQuoteOrHintBlock(block: Block | Inline | Document | Fragment): block is Block {
    return 'type' in block && (block.type === BLOCK_QUOTE || block.type === BLOCK_HINT);
}

function getQuoteOrHintRange(value: Value): TextRange | null {
    const block = getCurrentQuoteOrHintBlock(value);
    if (!block) {
        return null;
    }
    const firstText = block.getFirstText();
    const lastText = block.getLastText();
    if (!firstText || !lastText) {
        return null;
    }
    return TextRange.create().moveToRangeOf(firstText, lastText);
}
